import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = slug
    ? articles.filter((a) => a.category === slug)
    : [];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />
        </div>

        {/* Category Hero */}
        {currentCategory && (
          <section className="relative w-full h-[300px] md:h-[380px] flex items-center justify-center overflow-hidden">
            <img
              src={currentCategory.image}
              alt={currentCategory.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/50"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-2xl mx-auto">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
                <i className={`${currentCategory.icon} text-2xl text-white`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-white/75 max-w-lg mx-auto">
                {currentCategory.description}
              </p>
            </div>
          </section>
        )}

        {/* Category Switcher */}
        <section className="border-b border-background-200/60 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-4 pb-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700"
                        >
                          {tag}
                        </span>
                      ))}
                      <span className="text-xs text-foreground-400 ml-auto">
                        {article.readTime} 分钟阅读
                      </span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-xs text-foreground-400">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line text-xs"></i>
                      </div>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}